import React, { useState, useEffect, createContext, useContext, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams, useLocation } from "react-router-dom";
import { useEscClose } from "./hooks/useEscClose";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  BookOpen, Users, LogOut, Menu, X, ChevronRight, CheckCircle, 
  AlertCircle, Award, Play, ArrowLeft, Home, Settings, User,
  Lock, Eye, EyeOff, Loader2, GraduationCap, FileText, Clock,
  BarChart3, UserPlus, Trash2, Key, Volume2, VolumeX, Pause,
  MessageCircle, Send, RotateCcw, Sparkles, ClipboardList, Download, Target, Video, Shield, PenTool,
  PlayCircle, CheckCircle2, ArrowRight, Bot, ArrowUpRight, Layers
} from "lucide-react";
import ReactMarkdown from 'react-markdown';
import TrainerPage from './pages/TrainerPage';
import FlashcardTrainerPage from './pages/FlashcardTrainerPage';
import ModuleContentViewer from './components/ModuleContentViewer';
import PDLLearningWrapper from './components/PDLLearningWrapper';
import ProgressGapsView from './components/ProgressGapsView';
import ModuleCompleteModal from './components/ModuleCompleteModal';
import AdminLiveSessionManager from './components/AdminLiveSessionManager';
import AdminParticipantsDashboard from './components/AdminParticipantsDashboard';
// Refactored Page Components
import AdminDashboardPage from './pages/AdminDashboard';
import AdminUsersPageComponent from './pages/AdminUsersPage';
import AdminCoursesPageComponent from './pages/AdminCoursesPage';
import AdminLiveSessionsPageComponent from './pages/AdminLiveSessionsPage';
import AdminFacharbeitenPage from './pages/AdminFacharbeitenPage';
import AdminExamsPage from './pages/AdminExamsPage';
import AdminVorlagenPage from './pages/AdminVorlagenPage';
import AdminTagesmaterialPage from './pages/AdminTagesmaterialPage';
import AdminScriptsPage from './pages/AdminScriptsPage';
import AdminRenderPage from './pages/AdminRenderPage';
import AdminAnwesenheitPage from './pages/AdminAnwesenheitPage';
import ExamRoom from './pages/ExamRoom';
import VerifyCertificatePage from './pages/VerifyCertificatePage';
import AccessibilityPage from './pages/AccessibilityPage';
import MyCertificatesPage from './pages/MyCertificatesPage';
import AdminDataProtectionComponent from './pages/AdminDataProtection';
import AdminContentEnhancerPage from './pages/AdminContentEnhancerPage';
import AdminSystemMigrationPage from './pages/AdminSystemMigrationPage';
import QuizViewPage from './pages/QuizView';
import CourseViewPage from './pages/CourseView';
import SignaturPage from './pages/SignaturPage';
import ModuleViewPage from './pages/ModuleView';
import StreakCard from './components/StreakCard';
import GalleryWall from './components/GalleryWall';
import { KalenderPromoCard } from './components/KalenderPromoCard';
import { isPflegeKiTarget } from './lib/userRole';
import { HERO_IMAGE, getCourseCover } from './lib/courseCovers';
import { AuthContext, useAuth, AuthProvider } from './contexts/AuthContext';
import LoginPage, { SetupPage } from './pages/LoginPage';
import './dickies-theme.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Auth Context — imported from ./contexts/AuthContext

// Logo Component - using the official logo
const Logo = ({ size = "default" }) => {
  const sizes = {
    small: "w-8 h-8",
    default: "w-12 h-12",
    large: "w-20 h-20"
  };
  
  return (
    <img 
      src="/dh_logo_terra.png"
      alt="Dickies Held"
      className={`${sizes[size]} rounded-xl object-contain`}
    />
  );
};

// Chat with Dickies Held Component
const DickiesChat = ({ moduleId = null, moduleTitle = "" }) => {
  const { user, getAuthHeader } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => `chat-${Date.now()}`);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await axios.post(
        `${API}/chat`,
        {
          message: userMessage,
          module_id: moduleId,
          session_id: sessionId
        },
        getAuthHeader()
      );

      setMessages(prev => [...prev, { role: "assistant", content: response.data.response }]);
    } catch (error) {
      console.error("Chat error:", error);
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: "Entschuldigung, da ist etwas schiefgelaufen. Bitte versuche es nochmal!" 
      }]);
    }
    setIsLoading(false);
  };

  const resetChat = async () => {
    try {
      await axios.delete(`${API}/chat/history/${sessionId}`, getAuthHeader());
      setMessages([]);
      toast.success("Chat zurückgesetzt");
    } catch (error) {
      console.error("Reset error:", error);
    }
  };

  const suggestedQuestions = moduleId ? [
    "Erkläre mir die wichtigsten Punkte nochmal",
    "Was muss ich für die Prüfung wissen?",
    "Kannst du ein Beispiel geben?"
  ] : [
    "Wie funktioniert die Plattform?",
    "Was ist Wundmanagement?",
    "Welche Module gibt es?"
  ];

  return (
    <>
      {/* Chat Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-24 right-6 z-50 w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-all duration-300 ${
          isOpen 
            ? 'bg-cream-200 text-sand-700 rotate-0' 
            : 'bg-gradient-to-br from-[#D4C4B0] to-[#A89F91] text-[#0A0A0A] hover:scale-110'
        }`}
        data-testid="chat-toggle"
      >
        {isOpen ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      {/* Chat Panel */}
      {isOpen && (
        <div 
          className="fixed bottom-40 right-6 z-50 w-[380px] max-w-[calc(100vw-3rem)] bg-cream-100 border border-[#2A2A2A] rounded-2xl shadow-2xl overflow-hidden animate-fade-in"
          data-testid="chat-panel"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#1F1F1F] to-[#111] p-4 border-b border-[#2A2A2A]">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-full flex items-center justify-center font-display text-[15px] flex-shrink-0" style={{ backgroundColor: '#E07A5F', color: '#F5F0EA' }}>DH</span>
              <div className="flex-1">
                <h3 className="font-display text-[16px]" style={{ color: '#F5F0EA' }}>Die Dozentin</h3>
                <p className="text-xs text-[#666]">
                  {moduleTitle ? `Hilfe zu: ${moduleTitle.substring(0, 25)}...` : "Deine Lernbegleiterin"}
                </p>
              </div>
              <button
                onClick={resetChat}
                className="p-2 text-[#666] hover:text-sand-700 rounded-lg hover:bg-cream-200 transition-colors"
                title="Chat zurücksetzen"
                data-testid="reset-chat"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="h-80 overflow-y-auto p-4 space-y-4 bg-cream-50">
            {messages.length === 0 ? (
              <div className="text-center py-4">
                <span className="mx-auto w-12 h-12 rounded-full flex items-center justify-center font-display text-[17px]" style={{ backgroundColor: '#E07A5F', color: '#F5F0EA', display: 'inline-flex' }}>DH</span>
                <p className="font-display text-lg text-espresso mt-4">
                  Frag die Dozentin.
                </p>
                <p className="text-sand-700 mb-4 text-sm">
                  Stell eine Frage zu deinem Kapitel — oder wähle einen Vorschlag.
                </p>
                <div className="space-y-2">
                  {suggestedQuestions.map((q, i) => (
                    <button
                      key={i}
                      onClick={() => { setInput(q); inputRef.current?.focus(); }}
                      className="block w-full text-left px-3 py-2 text-sm text-sand-700 bg-cream-200 rounded-lg hover:bg-cream-300 hover:text-terra-dark transition-colors border border-[#2A2A2A]"
                      data-testid={`suggestion-${i}`}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] px-4 py-2.5 rounded-2xl ${
                      msg.role === 'user'
                        ? 'bg-terra text-[#0A0A0A] rounded-br-md'
                        : 'bg-cream-200 text-sand-800 rounded-bl-md'
                    }`}
                  >
                    <div className="text-sm leading-relaxed whitespace-pre-wrap">
                      {msg.content}
                    </div>
                  </div>
                </div>
              ))
            )}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-cream-200 text-sand-800 px-4 py-3 rounded-2xl rounded-bl-md">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-terra-dark" />
                    <span className="text-sm text-sand-700">Dickies Held denkt nach...</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form onSubmit={sendMessage} className="p-4 border-t border-[#2A2A2A] bg-cream-100">
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Stelle eine Frage..."
                className="flex-1 px-4 py-2.5 bg-cream-200 border border-[#2A2A2A] rounded-xl text-sand-800 placeholder-sand-500 focus:outline-none focus:border-[#D4C4B0] transition-colors"
                disabled={isLoading}
                data-testid="chat-input"
              />
              <button
                type="submit"
                disabled={!input.trim() || isLoading}
                className="px-4 py-2.5 bg-terra text-[#0A0A0A] rounded-xl hover:bg-terra-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                data-testid="chat-send"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

// Progress Bar Component
const ProgressBar = ({ value, className = "" }) => {
  return (
    <div className={`h-2 bg-cream-200 rounded-full overflow-hidden ${className}`}>
      <div 
        className="h-full bg-gradient-to-r from-[#D4C4B0] to-[#A89F91] progress-fill rounded-full"
        style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
      />
    </div>
  );
};

// Button Component
const Button = ({ children, variant = "primary", size = "default", disabled, loading, className = "", ...props }) => {
  const variants = {
    primary: "bg-terra text-[#0A0A0A] hover:bg-terra-dark",
    secondary: "bg-cream-200 text-sand-800 hover:bg-cream-300 border border-[#2A2A2A]",
    ghost: "bg-transparent text-sand-700 hover:bg-cream-200 hover:text-terra-dark",
    destructive: "bg-red-700 text-sand-800 hover:bg-red-800"
  };
  
  const sizes = {
    small: "px-3 py-1.5 text-sm",
    default: "px-4 py-2",
    large: "px-6 py-3 text-lg"
  };
  
  return (
    <button
      className={`${variants[variant]} ${sizes[size]} rounded-lg font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${className}`}
      disabled={disabled || loading}
      {...props}
    >
      {loading && <Loader2 className="w-4 h-4 animate-spin" />}
      {children}
    </button>
  );
};

// Input Component
const Input = ({ label, error, type = "text", className = "", ...props }) => {
  const [showPassword, setShowPassword] = useState(false);
  const isPassword = type === "password";
  
  return (
    <div className={className}>
      {label && <label className="block text-sm font-medium text-sand-700 mb-2">{label}</label>}
      <div className="relative">
        <input
          type={isPassword && showPassword ? "text" : type}
          className="w-full px-4 py-3 bg-cream-200 border border-[#2A2A2A] rounded-lg text-sand-800 placeholder-sand-500 focus:outline-none focus:border-[#D4C4B0] focus:ring-1 focus:ring-[#D4C4B0] transition-all"
          {...props}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#666] hover:text-sand-700"
          >
            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        )}
      </div>
      {error && <p className="text-sm text-red-400 mt-1">{error}</p>}
    </div>
  );
};

// Card Component
const Card = ({ children, className = "", hover = false, ...props }) => {
  return (
    <div 
      className={`bg-cream-100 border border-[#2A2A2A] rounded-xl p-6 ${hover ? 'card-hover cursor-pointer' : ''} ${className}`}
      {...props}
    >
      {children}
    </div>
  );
};

// Login Page
const TESTIMONIALS = [
  {
    quote: 'Endlich eine Weiterbildung, die zu meinem Schichtplan passt. Die Live-Sessions donnerstags abends sind top — praxisnah, kein Blabla.',
    author: 'Sandra M.',
    role: 'PDL, Häusliche Krankenpflege',
    course: 'PDL-Weiterbildung Teil 1 & 2',
  },
  {
    quote: 'Ich war skeptisch bei Online-Kursen — aber die Aufgaben und das persönliche Feedback vom Dozenten haben mich überzeugt. Bin jetzt zertifizierte Wundexpertin.',
    author: 'Kirsten K.',
    role: 'Zertifizierte Wundexpertin',
    course: 'Weiterbildung Wundexperte',
  },
  {
    quote: 'Der Support antwortet innerhalb weniger Stunden — auch am Wochenende. Für uns Führungskräfte im Pflegedienst unbezahlbar.',
    author: 'Michael B.',
    role: 'Geschäftsführung Pflegedienst',
    course: 'Qualitätsbeauftragter',
  },
];

// Sidebar Component
const Sidebar = ({ isOpen, onClose }) => {
  const { user, logout, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [pendingCount, setPendingCount] = useState(0);
  
  const isAdmin = user?.role === "admin";
  
  // Fetch pending sessions count for admins
  useEffect(() => {
    if (isAdmin) {
      const fetchPending = async () => {
        try {
          const res = await axios.get(`${API}/admin/pending-sessions`, getAuthHeader());
          setPendingCount(res.data.pending_count || 0);
        } catch (error) {
          console.error("Error fetching pending sessions:", error);
        }
      };
      fetchPending();
      // Refresh every 60 seconds + sofort nach Freigabe-Aktionen
      const interval = setInterval(fetchPending, 60000);
      window.addEventListener('dh-pending-sessions-changed', fetchPending);
      return () => {
        clearInterval(interval);
        window.removeEventListener('dh-pending-sessions-changed', fetchPending);
      };
    }
  }, [isAdmin, getAuthHeader]);
  
  const menuItems = isAdmin ? [
    { icon: Home, label: "Heute", path: "/dashboard" },
    { icon: Users, label: "Teilnehmer", path: "/admin/users" },
    { icon: Video, label: "Live-Sessions", path: "/admin/live-sessions", badge: pendingCount },
    { icon: FileText, label: "Facharbeiten", path: "/admin/facharbeiten" },
    { icon: GraduationCap, label: "Prüfungen", path: "/admin/pruefungen" },
    { icon: BookOpen, label: "Kurse", path: "/admin/courses" },
    { icon: GraduationCap, label: "Lernansicht", path: "/lernansicht" },
    { icon: ClipboardList, label: "Vorlagen", path: "/admin/vorlagen" },
    { icon: FileText, label: "Skripte", path: "/admin/skripte" },
    { icon: Video, label: "Video-Produktion", path: "/admin/render" },
    { icon: ClipboardList, label: "Anwesenheit", path: "/admin/anwesenheit" },
    { icon: FileText, label: "Tagesmaterial", path: "/admin/tagesmaterial" },
    { icon: Sparkles, label: "Content Enhancer", path: "/admin/content-enhancer", system: true },
    { icon: Shield, label: "Datensicherheit", path: "/admin/data-protection", system: true },
    { icon: Settings, label: "System-Migration", path: "/admin/system-migration", system: true },
  ] : [
    { icon: Home, label: "Meine Kurse", path: "/dashboard" },
    { icon: BarChart3, label: "Mein Fortschritt", path: "/progress" },
    { icon: Target, label: "Fragen-Trainer", path: "/trainer" },
    { icon: Layers, label: "Lernkarten", path: "/lernkarten" },
    { icon: Award, label: "Meine Zertifikate", path: "/my-certificates" },
    { icon: Sparkles, label: "Was darf ich lernen?", action: "open-qualification-check", highlight: true },
  ];

  const showPflegeKi = !isAdmin && isPflegeKiTarget(user);

  const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII'];
  const QUOTES = [
    'Wissen ist das Einzige, das mehr wird, wenn man es teilt.',
    'Pflege ist Kunst — und Kunst braucht Übung.',
    'Jede Seite, die du heute liest, trägt morgen jemand anderen.',
    'Kleine Schritte, jeden Tag — das ist der ganze Trick.',
  ];
  const dayOfYear = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0)) / 86400000);
  const quote = QUOTES[dayOfYear % QUOTES.length];

  const [reading, setReading] = useState(null);
  useEffect(() => {
    const readStorage = () => {
      try {
        const r = JSON.parse(localStorage.getItem('dh_current_reading') || 'null');
        setReading(r?.id ? r : null);
      } catch { setReading(null); }
    };
    readStorage();
    window.addEventListener('dh-reading-updated', readStorage);
    return () => window.removeEventListener('dh-reading-updated', readStorage);
  }, [location.pathname]);

  const GRAIN_BG = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='2'/%3E%3C/filter%3E%3Crect width='160' height='160' filter='url(%23n)'/%3E%3C/svg%3E")`;

  const regularItems = menuItems.filter((i) => !i.highlight && !i.system);
  const systemItems = menuItems.filter((i) => i.system);
  const specialItems = menuItems.filter((i) => i.highlight);
  const roleLabel = isAdmin ? 'Admin' : 'Teilnehmerin';

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar — "Der Buchrücken" */}
      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-50
          w-[264px] flex-shrink-0
          transform transition-transform duration-300
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
        style={{ backgroundColor: '#F9E7DC', borderRight: '1px solid #F0D4C2' }}
      >
        <div className="absolute inset-0 pointer-events-none" style={{ opacity: 0.03, backgroundImage: GRAIN_BG }} aria-hidden />
        <div className="relative flex flex-col h-full overflow-y-auto">
          {/* Header */}
          <div className="px-6 pt-7 pb-0">
            <div className="flex items-center gap-3">
              <img src="/dh_logo_terra.png" alt="" className="w-10 h-10 rounded-lg object-contain flex-shrink-0" />
              <h2 className="font-display text-[21px] leading-tight" style={{ color: '#522A25' }}>
                Dickies-<span className="italic" style={{ color: '#E07A5F' }}>Campus</span>
              </h2>
            </div>
            <p className="text-[10px] uppercase font-semibold mt-3" style={{ letterSpacing: '0.3em', color: '#C4614A' }}>
              {isAdmin ? 'Admin-Bereich' : 'Akademie für Pflege'}
            </p>
            <div className="mt-3" style={{ width: 40, height: 1, backgroundColor: '#E07A5F' }} />
          </div>
          
          {/* Navigation — Inhaltsverzeichnis */}
          <nav className="px-0 space-y-0.5" style={{ marginTop: 40 }}>
            {regularItems.map((item, idx) => {
              const handleClick = () => {
                if (item.action) {
                  window.dispatchEvent(new CustomEvent(item.action));
                  onClose();
                  return;
                }
                navigate(item.path);
                onClose();
              };
              const isActive = item.path && location.pathname === item.path;
              return (
                <button
                  key={item.path || item.action}
                  onClick={handleClick}
                  className="group relative w-full flex items-center gap-3 py-2.5 pl-6 pr-4 text-left"
                  data-testid={`nav-${(item.path || item.action).replace(/\//g, '-')}`}
                >
                  {isActive && <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-terra" />}
                  <span className="font-display text-[15px] w-7 flex-shrink-0" style={{ color: '#E07A5F' }}>
                    {ROMAN[idx]}
                  </span>
                  <span
                    className="font-heading text-sm transition-all duration-250 group-hover:translate-x-1"
                    style={{
                      color: isActive ? '#522A25' : 'rgba(82,42,37,0.72)',
                      fontWeight: isActive ? 600 : 400,
                      transition: 'transform 250ms ease, color 250ms ease',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = '#522A25'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = isActive ? '#522A25' : 'rgba(82,42,37,0.72)'; }}
                  >
                    {item.label}
                  </span>
                  {item.badge > 0 && (
                    <span className="ml-auto px-2 py-0.5 text-[10px] font-bold rounded-full bg-terra text-white">
                      {item.badge}
                    </span>
                  )}
                </button>
              );
            })}

            {/* System-Bereich — selten genutzt, optisch zurückgenommen */}
            {systemItems.length > 0 && (
              <div className="mt-6">
                <p className="pl-6 mb-1.5 text-[9px] uppercase font-semibold" style={{ letterSpacing: '0.3em', color: 'rgba(82,42,37,0.35)' }}>
                  System
                </p>
                {systemItems.map((item) => {
                  const isActive = item.path && location.pathname === item.path;
                  return (
                    <button
                      key={item.path}
                      onClick={() => { navigate(item.path); onClose(); }}
                      className="group relative w-full flex items-center gap-3 py-2 pl-6 pr-4 text-left"
                      data-testid={`nav-${item.path.replace(/\//g, '-')}`}
                    >
                      {isActive && <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-terra" />}
                      <span
                        className="font-heading text-[13px] transition-all duration-250 group-hover:translate-x-1"
                        style={{
                          color: isActive ? '#522A25' : 'rgba(82,42,37,0.5)',
                          transition: 'transform 250ms ease, color 250ms ease',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = '#522A25'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = isActive ? '#522A25' : 'rgba(82,42,37,0.5)'; }}
                      >
                        {item.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Marginalie: Was darf ich lernen? */}
            {specialItems.map((item) => (
              <button
                key={item.action}
                onClick={() => { window.dispatchEvent(new CustomEvent(item.action)); onClose(); }}
                className="group w-full text-left py-3 pl-6 pr-4 mt-2"
                data-testid={`nav-${item.action.replace(/\//g, '-')}`}
              >
                <span
                  className="font-display italic text-[15px] inline-block transition-transform duration-250 group-hover:translate-x-1"
                  style={{ color: '#C4614A', transition: 'transform 250ms ease' }}
                >
                  <span className="not-italic mr-1.5" style={{ fontSize: 11 }}>✳</span>
                  {item.label}
                </span>
              </button>
            ))}
          </nav>

          {/* Die Mitte: Aktuelle Lektüre + Zitat */}
          <div className="flex-1 px-6 pt-10">
            {!isAdmin && reading && (
              <button
                onClick={() => { navigate(`/course/${reading.id}`); onClose(); }}
                className="group w-full text-left"
                data-testid="sidebar-current-reading"
              >
                <p className="text-[10px] uppercase font-semibold mb-2.5" style={{ letterSpacing: '0.28em', color: '#C4614A' }}>
                  Aktuelle Lektüre
                </p>
                <p
                  className="font-display text-[15px] leading-snug transition-transform duration-250 group-hover:translate-x-1"
                  style={{ color: '#522A25', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', transition: 'transform 250ms ease' }}
                >
                  {reading.title}
                </p>
                {!reading.bg && (
                  <div className="mt-2.5 h-[2px] rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(82,42,37,0.12)' }}>
                    <div className="h-full" style={{ width: `${reading.total ? Math.round((reading.done / reading.total) * 100) : 0}%`, backgroundColor: '#E07A5F' }} />
                  </div>
                )}
                <p className="mt-1.5 text-[11px]" style={{ color: 'rgba(82,42,37,0.5)' }}>
                  {reading.bg
                    ? `${reading.tage || '–'} Unterrichtstage · Online-Präsenzkurs`
                    : `Kapitel ${reading.done || 0} von ${reading.total || '–'}`}
                </p>
              </button>
            )}

            <div className={!isAdmin && reading ? 'mt-12' : ''}>
              <div style={{ width: 40, height: 1, backgroundColor: 'rgba(224,122,95,0.6)' }} />
              <p className="font-display italic text-[14px] leading-relaxed mt-3" style={{ color: 'rgba(82,42,37,0.6)' }}>
                „{quote}"
              </p>
            </div>
          </div>
          
          {/* Fuß */}
          <div className="px-6 pt-6 pb-6 mt-6" style={{ borderTop: '1px solid #F0D4C2' }}>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-terra flex items-center justify-center font-display text-[15px] text-white flex-shrink-0">
                {(user?.name || '?').charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-display text-[15px] truncate" style={{ color: '#522A25' }}>{user?.name}</p>
                <p className="text-[9px] uppercase font-semibold" style={{ letterSpacing: '0.25em', color: 'rgba(82,42,37,0.45)' }}>{roleLabel}</p>
              </div>
            </div>
            <button 
              className="group inline-flex items-center gap-1.5 text-[13px] transition-colors"
              style={{ color: 'rgba(82,42,37,0.5)' }}
              onMouseEnter={(e) => { e.currentTarget.style.color = '#E07A5F'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(82,42,37,0.5)'; }}
              onClick={() => { logout(); navigate("/"); }}
              data-testid="logout-button"
            >
              Abmelden
              <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
            </button>

            {/* Pflege-KI cross-sell — only for leitende Rollen */}
            {showPflegeKi && (
              <a
                href={`https://dickies-helden.de/preise?utm_source=learn&utm_medium=sidebar&utm_campaign=pdl-suite&utm_content=${encodeURIComponent(user?.profession || 'unknown')}#pflege-ki`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 text-sm inline-flex items-center gap-2 transition-colors w-full"
                style={{ color: 'rgba(82,42,37,0.7)' }}
                data-testid="sidebar-pflege-ki"
              >
                <Bot className="h-4 w-4 flex-shrink-0" style={{ color: '#E07A5F' }} strokeWidth={1.7} />
                <span>Pflege-KI</span>
                <span className="ml-auto text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-bold leading-none" style={{ backgroundColor: 'rgba(224,122,95,0.15)', color: '#C4614A' }}>
                  Neu
                </span>
                <ArrowUpRight className="h-3 w-3" style={{ color: 'rgba(82,42,37,0.4)' }} />
              </a>
            )}

            {/* Cross-sell discovery link — Gold-Outline-Pill */}
            <a
              href="https://dickies-helden.de/#kurse?utm_source=learn&utm_medium=sidebar&utm_campaign=discovery"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 h-10 rounded-full text-[12px] font-semibold uppercase flex items-center justify-center gap-2 transition-colors"
              style={{ border: '1px solid rgba(224,122,95,0.6)', color: '#C4614A', letterSpacing: '0.12em' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(224,122,95,0.1)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              data-testid="sidebar-discovery-link"
            >
              Neue Kurse entdecken
              <ArrowRight className="h-3 w-3" />
            </a>

            <p className="mt-5 text-center text-[9px] uppercase font-semibold select-none" style={{ letterSpacing: '0.3em', color: 'rgba(82,42,37,0.35)' }}>
              Campus · MMXXVI
            </p>
          </div>
        </div>
      </aside>
    </>
  );
};

// Top Bar — rotates between 3 marketing touchpoints (Atlas, Quick-Check, Pflege-KI).
// Pauses on hover. Dismiss expires after 7 days.
const TOPBAR_DISMISS_KEY = 'dh_topbar_dismissed_at';
const TOPBAR_DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

const TopBar = ({ onOpenQualificationCheck }) => {
  const { user } = useAuth();
  const isAdminUser = user?.role === 'admin';
  const [dismissed, setDismissed] = useState(() => {
    try {
      const ts = parseInt(localStorage.getItem(TOPBAR_DISMISS_KEY) || '0', 10);
      if (!ts) return false;
      return (Date.now() - ts) < TOPBAR_DISMISS_TTL_MS;
    } catch {
      return false;
    }
  });
  const [index, setIndex] = useState(0);
  const [hover, setHover] = useState(false);

  // Messages: Atlas (everyone) · Quick-Check (everyone) · Pflege-KI (everyone — gating happens at landing page)
  const messages = React.useMemo(() => [
    {
      key: 'atlas',
      icon: BookOpen,
      label: 'Kostenloses Praxis-Handbuch:',
      cta: '\u201EWer darf was in der Pflege\u201C jetzt downloaden \u2192',
      href: 'https://dickies-helden.de/#kurse?utm_source=learn&utm_medium=topbar&utm_campaign=lead-magnet&utm_content=atlas',
      testid: 'topbar-lead-magnet',
    },
    {
      key: 'quickcheck',
      icon: Sparkles,
      label: 'Was darf ich lernen?',
      cta: 'Quick-Check starten \u2192',
      onClick: onOpenQualificationCheck,
      testid: 'topbar-quickcheck',
    },
    {
      key: 'pflege-ki',
      icon: Bot,
      label: 'Neu f\u00FCr Leitung:',
      cta: 'Pflege-KI 14 Tage gratis testen \u2192',
      href: `https://dickies-helden.de/preise?utm_source=learn&utm_medium=topbar&utm_campaign=pdl-suite&utm_content=${encodeURIComponent(user?.profession || user?.role || 'unknown')}#pflege-ki`,
      testid: 'topbar-pflege-ki',
    },
  ], [onOpenQualificationCheck, user]);

  useEffect(() => {
    if (dismissed || hover) return undefined;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % messages.length);
    }, 6000);
    return () => clearInterval(id);
  }, [dismissed, hover, messages.length]);

  if (dismissed || isAdminUser) return null;

  const dismiss = () => {
    try { localStorage.setItem(TOPBAR_DISMISS_KEY, String(Date.now())); } catch (e) { /* ignore */ }
    setDismissed(true);
  };

  const current = messages[index];
  const Icon = current.icon;

  const inner = (
    <>
      <Icon className="h-3.5 w-3.5 flex-shrink-0" style={{ color: '#C4614A' }} strokeWidth={2} />
      <span className="hidden sm:inline" style={{ color: 'rgba(82,42,37,0.75)' }}>{current.label}</span>
      <span className="font-medium underline transition-colors" style={{ color: '#C4614A', textDecorationColor: 'rgba(196,97,74,0.5)' }}>
        {current.cta}
      </span>
    </>
  );

  return (
    <div
      className="text-xs sm:text-sm"
      style={{ backgroundColor: '#F9E7DC', borderBottom: '1px solid #F0D4C2' }}
      data-testid="topbar"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2 flex items-center justify-center gap-2 sm:gap-3 relative">
        <div
          key={current.key}
          className="flex items-center gap-1.5 sm:gap-2 animate-fade-in"
          data-testid={current.testid}
        >
          {current.href ? (
            <a
              href={current.href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 sm:gap-2"
            >
              {inner}
            </a>
          ) : (
            <button
              type="button"
              onClick={current.onClick}
              className="inline-flex items-center gap-1.5 sm:gap-2"
            >
              {inner}
            </button>
          )}
        </div>

        {/* Dots indicator */}
        <div className="hidden sm:flex items-center gap-1 ml-2" aria-hidden="true">
          {messages.map((m, i) => (
            <span
              key={m.key}
              className={`h-1 w-1 rounded-full transition-colors ${i === index ? 'bg-terra' : 'bg-terra/25'}`}
            />
          ))}
        </div>

        <button
          onClick={dismiss}
          aria-label="Schlie\u00DFen"
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 transition-colors"
          style={{ color: 'rgba(82,42,37,0.5)' }}
          data-testid="topbar-dismiss"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
};

// Qualification Quick-Check Modal
const QualificationCheck = ({ open, onClose }) => {
  const [selected, setSelected] = useState(null);
  useEscClose(onClose, open);
  if (!open) return null;

  // Qualifications and which courses each can attend
  // Note: Bitte mit Geschäftsleitung gegenprüfen — wir konfigurieren das später feiner.
  const qualifications = [
    {
      id: 'fachkraft',
      label: 'Pflegefachkraft',
      sub: 'Examiniert · 3-jährige Ausbildung',
      eligible: 'all',
      summary: 'Du darfst alle bei uns angebotenen Weiterbildungen besuchen — von Basis bis Leitungsfunktionen.',
      courses: [
        { title: 'PDL-Weiterbildung Teil 1 & 2', ue: '460 + 260 UE', slug: 'pdl' },
        { title: 'Praxisanleitung', ue: '300 UE · §4 PflAPrV', slug: 'praxisanleitung' },
        { title: 'Wundexperte', ue: '84 UE · interne Prüfung', slug: 'wundexperte' },
        { title: 'Wundtherapeut', ue: '168 UE · interne Prüfung', slug: 'wundtherapeut' },
        { title: 'Palliativpflege', ue: '120 UE · DGP-konform', slug: 'palliativpflege' },
        { title: 'Behandlungspflege LG1 + LG2', ue: '208 UE', slug: 'behandlungspflege' },
        { title: 'Gerontopsychiatrische Fachweiterbildung', ue: '480 UE', slug: 'gerontopsychiatrie' },
      ],
    },
    {
      id: 'helfer-erfahren',
      label: 'Pflegehelfer · mit Berufserfahrung',
      sub: 'Mind. 1 Jahr nachweisbare Erfahrung',
      eligible: 'subset',
      summary: 'Mit einem Jahr Berufserfahrung qualifizierst du dich für die Behandlungspflege LG1 und LG2.',
      courses: [
        { title: 'Behandlungspflege LG1 + LG2', ue: '208 UE', slug: 'behandlungspflege' },
        { title: 'Hygienebeauftragter in der Pflege', ue: '80 UE', slug: 'hygienebeauftragter' },
        { title: 'Jahresfortbildungen (Pflicht)', ue: '8 UE p.a.', slug: 'jahresfortbildungen' },
      ],
    },
    {
      id: 'helfer-anfaenger',
      label: 'Pflegehelfer · ohne Berufserfahrung',
      sub: 'Einstieg oder Quereinstieg',
      eligible: 'basic',
      summary: 'Für den Einstieg empfehlen wir Grundlagenkurse sowie die Spezialisierungen Palliativ, Hygiene und QM.',
      courses: [
        { title: 'Ambulanter Pflegeassistent', ue: 'Basismodul', slug: 'pflegeassistent' },
        { title: 'Weiterbildung Palliativpflege', ue: '120 UE', slug: 'palliativpflege' },
        { title: 'Qualitätsmanagementbeauftragter (QMB)', ue: '42 UE · DIN/ISO', slug: 'qmb' },
        { title: 'Hygienebeauftragter in der Pflege', ue: '80 UE', slug: 'hygienebeauftragter' },
      ],
    },
  ];

  const currentQual = qualifications.find(q => q.id === selected);

  return (
    <div
      className="fixed inset-0 bg-espresso/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in"
      onClick={onClose}
      data-testid="qualification-check-modal"
    >
      <div
        className="bg-cream-50 max-w-2xl w-full rounded-3xl border border-cream-300 shadow-hover relative max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-sand-500 hover:text-sand-800 transition-colors z-10"
          aria-label="Schließen"
          data-testid="qualification-check-close"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="p-6 sm:p-8">
          <div className="text-[11.5px] uppercase tracking-[0.24em] text-peach-dark font-semibold mb-2">
            Quick-Check · Qualifikation
          </div>
          <h2 className="font-display text-3xl text-sand-800 leading-tight mb-2">
            Was darf ich lernen?
          </h2>
          <p className="text-sand-600 mb-6 leading-relaxed">
            Wähle deine aktuelle Qualifikation aus — wir zeigen dir, welche Weiterbildungen du belegen darfst.
          </p>

          {/* Qualification buttons */}
          <div className="grid sm:grid-cols-3 gap-2 mb-6">
            {qualifications.map(q => (
              <button
                key={q.id}
                onClick={() => setSelected(q.id)}
                className={`text-left p-3 rounded-xl border-2 transition-all ${
                  selected === q.id
                    ? 'border-terra bg-peach-50'
                    : 'border-cream-300 bg-cream-100 hover:border-peach-200'
                }`}
                data-testid={`qualification-${q.id}`}
              >
                <div className={`font-heading font-semibold text-sm ${selected === q.id ? 'text-sand-800' : 'text-sand-700'}`}>
                  {q.label}
                </div>
                <div className="text-xs text-sand-500 mt-0.5">{q.sub}</div>
              </button>
            ))}
          </div>

          {/* Result */}
          {currentQual && (
            <div className="bg-cream-100 border border-cream-300 rounded-2xl p-5 animate-fade-in" data-testid="qualification-result">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-sage/15 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="h-5 w-5 text-sage-dark" strokeWidth={1.8} />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading font-semibold text-sand-800 mb-1">
                    {currentQual.label}
                  </h3>
                  <p className="text-sm text-sand-600 leading-relaxed">{currentQual.summary}</p>
                </div>
              </div>

              <div className="space-y-2">
                {currentQual.courses.map((c, i) => (
                  <a
                    key={i}
                    href={`https://dickies-helden.de/#kurse?utm_source=learn&utm_medium=quickcheck&utm_content=${encodeURIComponent(currentQual.id)}&utm_campaign=qualification-match`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between gap-3 p-3 rounded-xl bg-cream-50 border border-cream-300 hover:border-terra/30 hover:shadow-soft transition-all group"
                    data-testid={`quickcheck-course-${c.slug}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sand-800 text-sm truncate">{c.title}</div>
                      <div className="text-xs text-sand-500 mt-0.5">{c.ue}</div>
                    </div>
                    <span className="inline-flex items-center gap-1 text-terra text-xs font-semibold group-hover:gap-2 transition-all flex-shrink-0">
                      Mehr <ArrowRight className="h-3.5 w-3.5" />
                    </span>
                  </a>
                ))}
              </div>

              <div className="mt-5 pt-4 border-t border-cream-300 flex flex-col sm:flex-row gap-2 items-start sm:items-center justify-between">
                <p className="text-xs text-sand-500">
                  Unsicher? Lade unser Handbuch herunter:
                </p>
                <a
                  href="https://dickies-helden.de/#kurse?utm_source=learn&utm_medium=quickcheck&utm_campaign=lead-magnet"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-terra font-semibold text-sm hover:text-terra-dark transition-colors"
                  data-testid="quickcheck-leadmagnet"
                >
                  {'\u201E'}Wer darf was{'\u201C'}-Handbuch <ArrowRight className="h-3.5 w-3.5" />
                </a>
              </div>
            </div>
          )}

          {!currentQual && (
            <div className="text-center text-sand-500 text-sm py-2">
              Wähle oben deine Qualifikation aus.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Layout Component
const Layout = ({ children, moduleId = null, moduleTitle = "" }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [qualCheckOpen, setQualCheckOpen] = useState(false);

  // Listen for global open-qualification-check events (Sidebar, Welcome-Card etc.)
  useEffect(() => {
    const handler = () => setQualCheckOpen(true);
    window.addEventListener('open-qualification-check', handler);
    return () => window.removeEventListener('open-qualification-check', handler);
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <a href="#main-content" className="dh-skip-link">Zum Inhalt springen</a>
      <TopBar onOpenQualificationCheck={() => setQualCheckOpen(true)} />
      <QualificationCheck open={qualCheckOpen} onClose={() => setQualCheckOpen(false)} />

      <div className="flex flex-1">
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

        <main id="main-content" className="flex-1 min-w-0 min-h-screen dh-bg-light">
          {/* Mobile header */}
          <header className="lg:hidden sticky top-0 z-30 bg-cream-50 border-b border-cream-300 p-4">
            <div className="flex items-center justify-between">
              <button 
                onClick={() => setSidebarOpen(true)}
                className="p-2 text-sand-600 hover:text-terra"
                data-testid="mobile-menu"
              >
                <Menu className="w-6 h-6" />
              </button>
              <Logo size="small" />
              <button
                onClick={() => setQualCheckOpen(true)}
                className="p-2 text-sand-600 hover:text-terra"
                aria-label="Was darf ich lernen?"
                data-testid="mobile-quickcheck"
              >
                <Sparkles className="w-5 h-5" />
              </button>
            </div>
          </header>

          <div className="p-4 md:p-6 lg:p-8 bg-cream-100 min-h-screen">
            {children}
          </div>
        </main>
      </div>
      
      {/* Dickies Chat - available on all pages */}
      <DickiesChat moduleId={moduleId} moduleTitle={moduleTitle} />
    </div>
  );
};

// User Dashboard
const UserDashboard = () => {
  const { user, getAuthHeader } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadCourses();
  }, []);

  const loadCourses = async () => {
    try {
      const response = await axios.get(`${API}/user/courses`, getAuthHeader());
      setCourses(response.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Kurse");
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-terra" />
        </div>
      </Layout>
    );
  }

  // Compute helpful aggregates for the welcome card
  const activeCourseCount = courses.length;
  const nextCourse = courses.find(c => (c.progress_percentage || 0) < 100) || courses[0];

  const formatGermanDate = (d) => d.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <Layout>
      {/* Atmosphere blobs */}
      <div className="fixed -top-20 -right-32 w-96 h-96 rounded-full bg-terra/[0.05] blur-3xl pointer-events-none -z-10" aria-hidden />
      <div className="fixed top-1/3 -left-32 w-80 h-80 rounded-full bg-cream-200/60 blur-3xl pointer-events-none -z-10" aria-hidden />

      <div className="max-w-6xl mx-auto animate-fade-in relative">
        {/* Welcome Hero — Editorial Magazine Spread */}
        <div className="mb-10 grid lg:grid-cols-[1.15fr_0.85fr] gap-8 lg:gap-12 items-center" data-testid="dashboard-hero">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="w-8 h-[2px] bg-terra" />
              <span className="text-[11px] uppercase tracking-[0.25em] font-bold text-terra">
                {formatGermanDate(new Date())}
              </span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-[3.4rem] text-espresso leading-[1.1] tracking-tight">
              Willkommen zurück, <br className="hidden sm:inline" />
              <span className="italic text-terra">{user?.name?.split(' ')[0] || user?.name}</span>.
            </h1>
            {activeCourseCount > 0 && nextCourse ? (
              <p className="text-lg text-espresso/60 mt-4 max-w-2xl">
                Du hast <strong className="text-espresso">{activeCourseCount} {activeCourseCount === 1 ? 'Kurs' : 'Kurse'}</strong> offen.
                Dein nächster Schritt:{' '}
                <button
                  onClick={() => navigate(`/course/${nextCourse.id}`)}
                  className="text-terra font-semibold hover:text-terra-dark inline-flex items-center gap-1 transition-colors underline-offset-4 hover:underline"
                  data-testid="welcome-next-course"
                >
                  {nextCourse.title} <ArrowRight className="h-4 w-4" />
                </button>
              </p>
            ) : (
              <p className="text-lg text-espresso/60 mt-4">
                Aktuell sind dir noch keine Kurse zugewiesen.
              </p>
            )}
            <div className="dh-palette-dots mt-6" aria-hidden data-testid="dashboard-palette-dots">
              {['#E07A5F', '#8A8D7F', '#C9968D', '#E9A0AC', '#522A25', '#D89232'].map((c) => (
                <span key={c} style={{ backgroundColor: c }} />
              ))}
            </div>
          </div>

          {/* Editorial hero image — framed art print (Desenio) */}
          <div className="hidden lg:block relative" data-testid="dashboard-hero-image">
            <div className="dh-frame rotate-1 hover:rotate-0 transition-transform duration-500">
              <div className="dh-mat overflow-hidden">
                <img
                  src={HERO_IMAGE}
                  alt="Dickies-Campus Magazin"
                  className="w-full h-64 object-cover object-[50%_30%]"
                />
              </div>
            </div>
            <p className="dh-gallery-caption mt-3.5">
              Ausgabe 2026 · Akademie für Pflege
            </p>
          </div>
        </div>

        {/* Lern-Streak */}
        <StreakCard className="mb-10" />

        {/* Kalender-Werbung */}
        <KalenderPromoCard getAuthHeader={getAuthHeader} />

        {/* Desenio Galerie-Wand */}
        <GalleryWall />

        {/* Courses Grid — Bento */}
        {courses.length === 0 ? (
          <div className="bg-cream-100 rounded-3xl border border-cream-200 p-16 text-center shadow-[0_8px_30px_-4px_rgba(0,0,0,0.04)]">
            <div className="w-20 h-20 mx-auto rounded-full bg-cream-100 flex items-center justify-center mb-5">
              <BookOpen className="w-10 h-10 text-terra" strokeWidth={1.5} />
            </div>
            <h3 className="font-display text-2xl text-espresso tracking-tight">Noch keine Kurse zugewiesen</h3>
            <p className="text-espresso/60 mt-2 max-w-md mx-auto">
              Wende dich an deine Kursleitung, um Zugang zu deinen Weiterbildungen zu erhalten.
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((course, i) => {
              const pct = Math.round(course.progress_percentage || 0);
              const isComplete = pct === 100;
              const isStarted = pct > 0 && !isComplete;
              const accent = ['#E07A5F', '#8A8D7F', '#C9968D'][i % 3];
              return (
                <div
                  key={course.id}
                  onClick={() => navigate(`/course/${course.id}`)}
                  data-testid={`course-card-${course.id}`}
                  style={{ animationDelay: `${i * 60}ms` }}
                  className="group bg-white border border-[#E0D6CA] rounded-sm overflow-hidden shadow-[0_8px_30px_-4px_rgba(82,42,37,0.06)] dh-lift hover:border-terra/40 cursor-pointer flex flex-col animate-fade-in-up"
                >
                  {/* Gallery label row */}
                  <div className="flex items-center justify-between px-5 pt-4 pb-3">
                    <span className="font-display text-[15px] leading-none" style={{ color: accent }}>
                      Nº {String(i + 1).padStart(2, '0')}
                    </span>
                    <span className="text-[9px] uppercase tracking-[0.28em] font-bold text-espresso/35">
                      Studio DH · MMXXVI
                    </span>
                  </div>

                  {/* Framed art-print image with passepartout */}
                  <div className="relative px-5">
                    <div className="dh-mat relative overflow-hidden">
                      <img
                        src={getCourseCover(course.id)}
                        alt=""
                        className="w-full h-40 object-cover group-hover:scale-[1.03] transition-transform duration-700"
                      />
                    </div>
                    <div className="absolute -bottom-6 right-9 bg-white rounded-full p-1 shadow-[0_6px_20px_-4px_rgba(60,36,20,0.25)] border border-[#E0D6CA]">
                      <ProgressRing pct={pct} size={52} stroke={5} />
                    </div>
                  </div>

                  <div className="p-6 pt-5 flex flex-col flex-1">
                  {/* Status kicker */}
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-4 h-[2px]" style={{ backgroundColor: accent }} />
                    <span className={`text-[10px] uppercase tracking-[0.2em] font-bold ${isComplete ? 'text-sage-dark' : isStarted ? 'text-terra' : 'text-espresso/40'}`}>
                      {isComplete ? 'Abgeschlossen' : isStarted ? 'In Bearbeitung' : 'Bereit zum Start'}
                    </span>
                  </div>

                  {/* Title + description */}
                  <h3 className="font-display text-[1.55rem] text-espresso leading-[1.12] tracking-tight mb-2 group-hover:text-terra transition-colors pr-10">
                    {course.title}
                  </h3>
                  <p className="text-espresso/60 text-sm leading-relaxed line-clamp-2 mb-6 flex-1">
                    {course.description}
                  </p>

                  {/* Footer meta */}
                  <div className="flex items-center justify-between pt-5 border-t border-cream-200">
                    <span className="inline-flex items-center gap-1.5 text-espresso/50 font-mono text-[10.5px] uppercase tracking-wider">
                      <BookOpen className="h-3.5 w-3.5" />
                      {course.completed_modules || 0} / {course.total_modules} Module
                    </span>
                    {course.has_certificate && (
                      <span className={`inline-flex items-center gap-1 font-mono text-[10.5px] uppercase tracking-wider ${isComplete ? 'text-sage-dark' : 'text-espresso/50'}`}>
                        <Award className="h-3.5 w-3.5" />
                        {isComplete ? 'Zertifikat bereit' : 'Zertifikat'}
                      </span>
                    )}
                  </div>

                  {/* Continue CTA (subtle) */}
                  <div className="mt-4 -mb-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="inline-flex items-center gap-1 text-sm font-semibold text-terra">
                      {isComplete ? 'Zertifikat ansehen' : isStarted ? 'Weiterlernen' : 'Kurs starten'}
                      <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                    </span>
                  </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Recommended section — Cross-sell to main shop */}
        {courses.length > 0 && (
          <section className="mt-12" data-testid="recommended-section">
            <div className="flex items-center gap-2.5 mb-1.5">
              <span className="w-6 h-[2px] bg-terra" />
              <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-terra">Weiterlesen · Ausgabe 2026</span>
            </div>
            <h2 className="font-display text-3xl text-espresso tracking-tight leading-tight mb-1.5">
              Empfohlen <span className="italic text-terra">für dich</span>.
            </h2>
            <p className="text-sand-600 text-sm mb-5">
              Basierend auf deinen aktuellen Kursen — direkt zur Buchung im Dickies-Campus-Shop.
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              {/* Card 1: Related course (Light) */}
              <a
                href="https://dickies-helden.de/#kurse?utm_source=learn&utm_medium=dashboard-reco&utm_campaign=cross-sell"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-cream-100 border border-cream-300 rounded-2xl p-6 hover:shadow-hover hover:border-peach-200 transition-all group"
                data-testid="reco-card-related"
              >
                <div className="text-[11px] uppercase tracking-wider text-peach-dark font-semibold mb-2">
                  Passt zu deinem Lernweg
                </div>
                <h3 className="font-heading font-semibold text-sand-800 mb-2 leading-snug">
                  Aufbau- und Spezialkurse entdecken
                </h3>
                <p className="text-sand-600 text-sm mb-3 leading-relaxed">
                  Vertiefe dein Wissen mit weiteren AZAV-zertifizierten Weiterbildungen für Pflegekräfte und Leitungen.
                </p>
                <span className="inline-flex items-center gap-1 text-terra font-semibold text-sm group-hover:gap-2 transition-all">
                  Mehr erfahren <ArrowRight className="h-4 w-4" />
                </span>
              </a>

              {/* Card 2: PDL Suite (Espresso highlight) */}
              <a
                href="https://dickies-helden.de/#kurse?utm_source=learn&utm_medium=dashboard-reco&utm_campaign=pdl-suite"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-espresso text-cream-100 rounded-2xl p-6 hover:scale-[1.01] hover:shadow-hover transition-all"
                data-testid="reco-card-pdl"
              >
                <div className="text-[11px] uppercase tracking-wider text-peach-200 font-semibold mb-2">
                  Für Leitungskräfte
                </div>
                <h3 className="font-heading font-semibold mb-2 leading-snug text-lg">
                  PDL-Suite mit Pflege-KI freischalten
                </h3>
                <p className="text-cream-200/85 text-sm mb-3 leading-relaxed">
                  Erweitere deine Weiterbildung um KI-Werkzeuge: SIS-Generator, Qualitäts-Dashboard und Praxisanleiter-Toolbox.
                </p>
                <span className="inline-flex items-center gap-1 text-cream-50 font-semibold text-sm">
                  Suite ansehen <ArrowRight className="h-4 w-4" />
                </span>
              </a>
            </div>
          </section>
        )}

        {/* PFLEGE-KI HERO CARD — only for Leitungs-Rollen */}
        {courses.length > 0 && isPflegeKiTarget(user) && (
          <section className="mt-10" data-testid="pflege-ki-hero">
            <div className="rounded-3xl bg-espresso text-cream-100 p-8 sm:p-10 relative overflow-hidden shadow-soft">
              {/* Subtile Lichteffekte */}
              <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-terra/20 blur-3xl pointer-events-none" />
              <div className="absolute -left-12 -bottom-20 w-56 h-56 rounded-full bg-peach-200/10 blur-3xl pointer-events-none" />

              <div className="relative grid lg:grid-cols-[1.5fr_auto] gap-6 items-center">
                <div className="space-y-4">
                  <div className="inline-flex items-center gap-2 text-[11.5px] uppercase tracking-[0.28em] text-peach-200 font-semibold">
                    <Sparkles className="h-3.5 w-3.5" />
                    Neu für Leitungskräfte
                  </div>

                  <h2 className="font-display text-3xl sm:text-4xl leading-[1.05] tracking-wide">
                    Pflege-KI: SIS schreibt sich von selbst
                  </h2>

                  <p className="text-cream-200/85 leading-relaxed max-w-xl text-[15px]">
                    Spar 4–6 Stunden Doku pro Woche.{' '}
                    <strong className="text-cream-50">SIS-Generator, MD-Simulator, Qualitäts-Dashboard</strong> —
                    alles QPR-2026-konform und mit Halluzinationsschutz. 14 Tage gratis testen.
                  </p>

                  <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-cream-200/95 pt-1">
                    <span className="inline-flex items-center gap-1.5">
                      <CheckCircle2 className="h-4 w-4 text-sage" strokeWidth={2} />
                      QPR-2026-konform
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <CheckCircle2 className="h-4 w-4 text-sage" strokeWidth={2} />
                      Halluzinationsschutz
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <CheckCircle2 className="h-4 w-4 text-sage" strokeWidth={2} />
                      DSGVO · Hosting Frankfurt
                    </span>
                  </div>
                </div>

                <a
                  href={`https://dickies-helden.de/preise?utm_source=learn&utm_medium=dashboard-card&utm_campaign=pdl-suite&utm_content=${encodeURIComponent(user?.profession || 'unknown')}#pflege-ki`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="pflege-ki-cta"
                  className="inline-flex items-center justify-center gap-2 bg-cream-50 hover:bg-cream-100 text-sand-800 rounded-full h-12 px-7 font-semibold shadow-soft hover:shadow-hover transition-all whitespace-nowrap"
                >
                  Jetzt testen
                  <ArrowRight className="h-5 w-5" strokeWidth={2} />
                </a>
              </div>
            </div>
          </section>
        )}
      </div>
    </Layout>
  );
};

// Circular Progress Ring (SVG)
const ProgressRing = ({ pct = 0, size = 80, stroke = 8 }) => {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (Math.max(0, Math.min(100, pct)) / 100) * circumference;
  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#EDE5DB"
          strokeWidth={stroke}
          fill="none"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#E07A5F"
          strokeWidth={stroke}
          strokeLinecap="round"
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 0.7s ease-out' }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="font-display text-xl text-sand-800 leading-none">
          {Math.round(pct)}%
        </span>
      </div>
    </div>
  );
};

// Course View
// CourseView moved to ./pages/CourseView.jsx (Feb 2026 refactor)
// ModuleView moved to ./pages/ModuleView.jsx (Feb 2026 refactor)


// Quiz View with Celebration Animation
// QuizView moved to ./pages/QuizView.jsx (Feb 2026 refactor)


// Admin Dashboard - Wrapper for refactored component
const AdminDashboard = () => {
  const { getAuthHeader } = useAuth();
  return <AdminDashboardPage Layout={Layout} getAuthHeader={getAuthHeader} />;
};

// Admin Users Page - Wrapper for refactored component
const AdminUsersPage = () => {
  const { getAuthHeader } = useAuth();
  return <AdminUsersPageComponent Layout={Layout} getAuthHeader={getAuthHeader} />;
};

// Admin Courses Page - Wrapper for refactored component
const AdminCoursesPage = () => {
  return <AdminCoursesPageComponent Layout={Layout} />;
};

// Admin Participants Page
const AdminParticipantsPage = () => {
  const { courseId } = useParams();
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();

  return (
    <Layout>
      <div className="max-w-6xl mx-auto animate-fade-in">
        <AdminParticipantsDashboard 
          courseId={courseId}
          authHeader={getAuthHeader()}
          onBack={() => navigate('/admin/courses')}
        />
      </div>
    </Layout>
  );
};

// Admin Live Sessions Page - Wrapper for refactored component
const AdminLiveSessionsPage = () => {
  const { getAuthHeader } = useAuth();
  return <AdminLiveSessionsPageComponent Layout={Layout} getAuthHeader={getAuthHeader} />;
};

// Admin Data Protection Page - Wrapper for refactored component
const AdminDataProtectionPage = () => {
  const { getAuthHeader } = useAuth();
  return <AdminDataProtectionComponent Layout={Layout} getAuthHeader={getAuthHeader} />;
};

// Progress Page
const TrainerPageWithLayout = () => (
  <Layout>
    <TrainerPage />
  </Layout>
);

const ProgressPage = () => {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [progress, setProgress] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProgress();
  }, []);

  const loadProgress = async () => {
    try {
      const [progressRes, coursesRes] = await Promise.all([
        axios.get(`${API}/user/progress`, getAuthHeader()),
        axios.get(`${API}/user/courses`, getAuthHeader())
      ]);
      setProgress(progressRes.data);
      setCourses(coursesRes.data);
    } catch (error) {
      toast.error("Fehler beim Laden");
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-terra-dark" />
        </div>
      </Layout>
    );
  }

  const completedModules = progress.filter(p => p.completed).length;
  const passedQuizzes = progress.filter(p => p.quiz_passed).length;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto animate-fade-in">
        {/* Editorial header */}
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-2">
            <span className="w-8 h-[2px] bg-terra" />
            <span className="text-[11px] uppercase tracking-[0.25em] font-bold text-terra">
              Dein Lernjournal · MMXXVI
            </span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-espresso leading-[1.0] tracking-tight uppercase">
            Mein <span className="text-terra">Fortschritt</span>.
          </h1>
          <p className="text-lg text-espresso/60 mt-4">Übersicht deiner Lernaktivitäten</p>
        </div>

        {/* Lern-Streak */}
        <StreakCard className="mb-8" />

        {/* Editorial stat panels */}
        <div className="grid sm:grid-cols-3 gap-5 mb-12">
          <div className="bg-cream-100 border border-cream-200 rounded-3xl p-6 shadow-soft" data-testid="stat-modules-completed">
            <div className="text-[10px] uppercase tracking-[0.2em] text-espresso/45 font-bold">
              Module abgeschlossen
            </div>
            <div className="font-display text-6xl text-espresso leading-none mt-3">{completedModules}</div>
            <div className="mt-4 pt-3 border-t border-cream-200 text-xs text-espresso/50">
              über alle deine Kurse
            </div>
          </div>
          <div className="bg-cream-100 border border-cream-200 rounded-3xl p-6 shadow-soft" data-testid="stat-quizzes-passed">
            <div className="text-[10px] uppercase tracking-[0.2em] text-espresso/45 font-bold">
              Quizze bestanden
            </div>
            <div className="font-display text-6xl text-terra leading-none mt-3">{passedQuizzes}</div>
            <div className="mt-4 pt-3 border-t border-cream-200 text-xs text-espresso/50">
              Wissen sitzt — weiter so
            </div>
          </div>
          <div className="bg-espresso rounded-3xl p-6 shadow-soft" data-testid="stat-active-courses">
            <div className="text-[10px] uppercase tracking-[0.2em] text-cream-100/60 font-bold">
              Aktive Kurse
            </div>
            <div className="font-display text-6xl text-cream-50 leading-none mt-3">{courses.length}</div>
            <div className="mt-4 pt-3 border-t border-cream-100/15 text-xs text-cream-100/60">
              Weiterbildung, die trägt
            </div>
          </div>
        </div>

        {/* Course Progress — editorial index */}
        <div className="flex items-center gap-2.5 mb-1.5">
          <span className="w-6 h-[2px] bg-terra" />
          <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-terra">Kurs-Index</span>
        </div>
        <h2 className="font-display text-3xl text-espresso uppercase tracking-tight leading-none mb-5">
          Kursfortschritt
        </h2>
        <div className="space-y-4">
          {courses.map((course, i) => {
            const pct = Math.round(course.progress_percentage || 0);
            const done = pct === 100;
            return (
              <div
                key={course.id}
                onClick={() => navigate(`/course/${course.id}`)}
                className="group bg-cream-100 border border-cream-200 rounded-2xl p-6 hover:border-terra/30 hover:shadow-hover transition-colors cursor-pointer"
                data-testid={`progress-course-${course.id}`}
              >
                <div className="flex items-center justify-between gap-4 mb-3">
                  <div className="flex items-baseline gap-4 min-w-0">
                    <span className="font-display text-xl text-terra leading-none flex-shrink-0">
                      Nº {String(i + 1).padStart(2, '0')}
                    </span>
                    <h3 className="font-heading font-semibold text-espresso truncate group-hover:text-terra transition-colors">
                      {course.title}
                    </h3>
                  </div>
                  <span className={`font-display text-2xl leading-none flex-shrink-0 ${done ? 'text-sage-dark' : 'text-espresso'}`}>
                    {pct}%
                  </span>
                </div>
                <div className="h-1.5 bg-cream-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${done ? 'bg-sage-dark' : 'bg-terra'}`}
                    style={{ width: `${pct}%`, transition: 'width 0.7s ease-out' }}
                  />
                </div>
                <div className="flex items-center justify-between mt-2.5">
                  <p className="text-xs text-espresso/50">
                    {course.completed_modules || 0} von {course.total_modules} Modulen
                  </p>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-terra opacity-0 group-hover:opacity-100 transition-opacity">
                    Zum Kurs <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Layout>
  );
};

// Protected Route
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream-50">
        <Loader2 className="w-8 h-8 animate-spin text-terra-dark" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/" replace />;
  }
  
  if (adminOnly && user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// A11y: Seitentitel je Route (Kurs/Modul setzen ihren Titel selbst dynamisch)
const ROUTE_TITLES = [
  ['/dashboard', 'Meine Kurse'],
  ['/progress', 'Mein Fortschritt'],
  ['/trainer', 'Fragen-Trainer'],
  ['/lernkarten', 'Lernkarten-Trainer'],
  ['/my-certificates', 'Meine Zertifikate'],
  ['/verify', 'Echtheitsprüfung'],
  ['/barrierefreiheit', 'Erklärung zur Barrierefreiheit'],
  ['/admin/users', 'Admin · Teilnehmer'],
  ['/admin/courses', 'Admin · Kurse'],
  ['/admin/participants', 'Admin · Teilnehmer'],
  ['/admin/live-sessions', 'Admin · Live-Sessions'],
  ['/admin/data-protection', 'Admin · Datensicherheit'],
  ['/admin/content-enhancer', 'Admin · Content Enhancer'],
  ['/admin/system-migration', 'Admin · System-Migration'],
  ['/setup', 'Einrichtung'],
];
const RouteTitle = () => {
  const location = useLocation();
  useEffect(() => {
    if (location.pathname.startsWith('/course/') || location.pathname.startsWith('/module/')) return;
    const match = ROUTE_TITLES.find(([p]) => location.pathname.startsWith(p));
    document.title = match ? `${match[1]} · Dickies-Campus` : 'Dickies-Campus · Lernplattform';
  }, [location.pathname]);
  return null;
};

// Main App
function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <RouteTitle />
        <Routes>
          <Route path="/" element={<LoginPage />} />
          <Route path="/setup" element={<SetupPage />} />
          
          <Route path="/verify" element={<VerifyCertificatePage />} />
          <Route path="/verify/:certNumber" element={<VerifyCertificatePage />} />
          <Route path="/barrierefreiheit" element={<AccessibilityPage />} />
          
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <DashboardRouter />
            </ProtectedRoute>
          } />
          <Route path="/lernansicht" element={
            <ProtectedRoute>
              <UserDashboard />
            </ProtectedRoute>
          } />
          
          <Route path="/course/:courseId" element={
            <ProtectedRoute>
              <CourseViewPage Layout={Layout} />
            </ProtectedRoute>
          } />
          
          <Route path="/signatur/:courseId" element={
            <ProtectedRoute>
              <SignaturPage Layout={Layout} />
            </ProtectedRoute>
          } />
          <Route path="/module/:moduleId" element={
            <ProtectedRoute>
              <ModuleViewPage Layout={Layout} />
            </ProtectedRoute>
          } />
          
          <Route path="/progress" element={
            <ProtectedRoute>
              <ProgressPage />
            </ProtectedRoute>
          } />
          
          <Route path="/trainer" element={
            <ProtectedRoute>
              <TrainerPageWithLayout />
            </ProtectedRoute>
          } />

          <Route path="/lernkarten" element={
            <ProtectedRoute>
              <Layout><FlashcardTrainerPage /></Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/my-certificates" element={
            <ProtectedRoute>
              <MyCertificatesPageWrapper />
            </ProtectedRoute>
          } />
          
          <Route path="/admin/users" element={
            <ProtectedRoute adminOnly>
              <AdminUsersPage />
            </ProtectedRoute>
          } />
          
          <Route path="/admin/courses" element={
            <ProtectedRoute adminOnly>
              <AdminCoursesPage />
            </ProtectedRoute>
          } />
          
          <Route path="/admin/participants/:courseId" element={
            <ProtectedRoute adminOnly>
              <AdminParticipantsPage />
            </ProtectedRoute>
          } />
          
          <Route path="/admin/live-sessions" element={
            <ProtectedRoute adminOnly>
              <AdminLiveSessionsPage />
            </ProtectedRoute>
          } />

          <Route path="/admin/facharbeiten" element={
            <ProtectedRoute adminOnly>
              <AdminFacharbeitenPage Layout={Layout} />
            </ProtectedRoute>
          } />

          <Route path="/admin/pruefungen" element={
            <ProtectedRoute adminOnly>
              <AdminExamsPage Layout={Layout} />
            </ProtectedRoute>
          } />

          <Route path="/admin/vorlagen" element={
            <ProtectedRoute adminOnly>
              <AdminVorlagenPage Layout={Layout} />
            </ProtectedRoute>
          } />

          <Route path="/admin/skripte" element={
            <ProtectedRoute adminOnly>
              <AdminScriptsPage Layout={Layout} />
            </ProtectedRoute>
          } />

          <Route path="/admin/render" element={
            <ProtectedRoute adminOnly>
              <AdminRenderPage Layout={Layout} />
            </ProtectedRoute>
          } />

          <Route path="/admin/anwesenheit" element={
            <ProtectedRoute adminOnly>
              <AdminAnwesenheitPage Layout={Layout} />
            </ProtectedRoute>
          } />

          <Route path="/admin/tagesmaterial" element={
            <ProtectedRoute adminOnly>
              <AdminTagesmaterialPage Layout={Layout} />
            </ProtectedRoute>
          } />

          <Route path="/exam/:attemptId" element={
            <ProtectedRoute>
              <ExamRoom />
            </ProtectedRoute>
          } />
          
          <Route path="/admin/data-protection" element={
            <ProtectedRoute adminOnly>
              <AdminDataProtectionPage />
            </ProtectedRoute>
          } />
          
          <Route path="/admin/content-enhancer" element={
            <ProtectedRoute adminOnly>
              <AdminContentEnhancerPage Layout={Layout} />
            </ProtectedRoute>
          } />
          
          <Route path="/admin/system-migration" element={
            <ProtectedRoute adminOnly>
              <AdminSystemMigrationPage Layout={Layout} />
            </ProtectedRoute>
          } />
          
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster 
        position="top-right" 
        toastOptions={{
          style: {
            background: '#1F1F1F',
            color: '#E5E5E5',
            border: '1px solid #2A2A2A'
          }
        }}
      />
    </AuthProvider>
  );
}

// My Certificates Page Wrapper
const MyCertificatesPageWrapper = () => {
  const { getAuthHeader } = useAuth();
  return (
    <Layout>
      <div className="max-w-5xl mx-auto animate-fade-in">
        <MyCertificatesPage getAuthHeader={getAuthHeader} />
      </div>
    </Layout>
  );
};

// Dashboard Router - routes to correct dashboard based on role
const DashboardRouter = () => {
  const { user } = useAuth();
  return user?.role === 'admin' ? <AdminDashboard /> : <UserDashboard />;
};

export default App;
